/**
 * @bos/portal3 — THE PERMISSIONS MATRIX (phase 2): who may do what, in one glance.
 *
 * The permissions review has always shown the roles as cards and the operations as a grid, and
 * both are true — but neither answers the question an owner actually arrives with ("can a
 * receptionist refund a payment?") without reading a paragraph per role. The grant matrix is the
 * shape that answers it: one row per permission the app actually uses, one column per role, one
 * mark per fact.
 *
 * IT IS THE SAME BYTES, FOLDED ONCE. The rows come from `permissionsInUse` and the ticks from
 * `permissionsHeldBy` — the SAME two folds the exact door's composer reads its dropdowns off
 * (`StageEdits.tsx`), extracted so the two can never disagree. A cell can therefore never offer
 * something the composer would refuse, and the matrix can never claim a grant the grant rows do
 * not carry.
 *
 * THE MATRIX OFFERS ONLY WHAT EXISTS. Clicking a cell compiles to `rb-grant-add` or
 * `rb-grant-remove`; a role's own header offers `rb-role-remove`. There is NO "add a role"
 * button, because there is no `rb-role-add` for it to compile to — drawing the control before the
 * door exists is exactly the lie this product refuses. A closed door draws the same matrix with
 * nothing to press.
 *
 * ACCESSIBILITY. It is a real `<table>` with `scope`d headers, so a screen reader announces
 * "receptionist · payment:write · allowed" from the cell itself; every cell says its state in
 * words as well as a mark, and while the door is open every cell is a real button in the tab
 * order. The role cards below stay: they carry the model's own justification sentence, which a
 * grid cannot hold.
 */
import type { PermissionsDoc } from '@bos/portal3-server';
import { permissionsHeldBy, permissionsInUse } from './StageEdits';

/** The three gestures, one per existing `rb-*` kind the matrix can honestly reach. */
export interface MatrixGestures {
  readonly onGrantAdd: (role: string, permission: string) => void;
  readonly onGrantRemove: (role: string, permission: string) => void;
  readonly onRoleRemove: (role: string) => void;
}

export interface PermissionsMatrixProps {
  readonly doc: PermissionsDoc;
  /** null = the door is closed: the matrix is a read surface and offers no change. */
  readonly gestures: MatrixGestures | null;
}

export function PermissionsMatrix({ doc, gestures }: PermissionsMatrixProps) {
  const permissions = permissionsInUse(doc);
  const roles = doc.roles;
  // Nothing to draw is nothing to draw: no empty frame, and the cards below still say everything.
  if (permissions.length === 0 || roles.length === 0) return null;

  const held = new Map(roles.map((r) => [r.id, new Set(permissionsHeldBy(doc, r.id))]));

  return (
    <div className="sr-mx-wrap">
      <table className="sr-mx" aria-label="What each role is allowed">
        <thead>
          <tr>
            <th scope="col" className="sr-mx__corner">
              Allowed to…
            </th>
            {roles.map((r) => (
              <th key={r.id} scope="col" className="sr-mx__rolehead">
                <span className="sr-mx__role">{r.id}</span>
                {r.canLogin === false && <span className="sr-chip">no login</span>}
                {gestures !== null && (
                  <button
                    className="sr-mx__rolebtn"
                    type="button"
                    data-gesture="role-remove"
                    data-role={r.id}
                    aria-label={`Remove the role ${r.id}`}
                    onClick={() => gestures.onRoleRemove(r.id)}
                  >
                    remove
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {permissions.map((p) => (
            <tr key={p}>
              <th scope="row" className="sr-mx__perm">
                {p}
              </th>
              {roles.map((r) => {
                const on = held.get(r.id)?.has(p) === true;
                const said = on ? `${r.id} may ${p}` : `${r.id} may not ${p}`;
                return (
                  <td key={r.id} className={on ? 'sr-mx__yes' : 'sr-mx__no'}>
                    {gestures !== null ? (
                      <button
                        className="sr-mx__cell"
                        type="button"
                        data-gesture={on ? 'grant-remove' : 'grant-add'}
                        data-role={r.id}
                        data-permission={p}
                        aria-pressed={on}
                        aria-label={on ? `${said} — stop this` : `${said} — allow it`}
                        onClick={() => (on ? gestures.onGrantRemove(r.id, p) : gestures.onGrantAdd(r.id, p))}
                      >
                        <span aria-hidden="true">{on ? '✓' : '·'}</span>
                      </button>
                    ) : (
                      <>
                        <span aria-hidden="true">{on ? '✓' : '·'}</span>
                        <span className="sr-mx__said">{said}</span>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
